import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { State, getDashboardCombined, getDashboardReport } from '../../reducers';
import { CombinedState as DashboardCombinedState } from '../../reducers/dashboard/reducers-dashboard';
import { map, shareReplay, pairwise, filter, startWith, distinctUntilChanged } from 'rxjs/operators';
import { Observable, combineLatest } from 'rxjs';
import { ReportService } from './report.service';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private dashboard$: Observable<DashboardCombinedState>;
  private dashboardLoaded$: Observable<DashboardCombinedState>;
  private dashboardLoadedNew$: Observable<DashboardCombinedState>;

  constructor(private readonly reportService: ReportService, private readonly store: Store<State>) {}

  public getDashboard(): Observable<DashboardCombinedState> {
    return this.getObservable().pipe(shareReplay(1));
  }

  public getLoadedDashboard(): Observable<DashboardCombinedState> {
    if (!this.dashboardLoaded$) {
      this.dashboardLoaded$ = this.getObservable().pipe(
        startWith(null),
        pairwise(),
        filter(([prev, next]) => !prev || (!next.report.updateRequired && prev.report.updateRequired)),
        map(([_, dashboardState]) => dashboardState),
        shareReplay(1)
      );
    }

    return this.dashboardLoaded$;
  }

  /**
   * skips replayed value on subscription if state value changed
   **/
  public getNewLoadedDashboard(): Observable<DashboardCombinedState> {
    if (!this.dashboardLoadedNew$) {
      this.dashboardLoadedNew$ = combineLatest([
        this.getLoadedDashboard(),
        this.store.pipe(select(getDashboardReport))]
      ).pipe(
        filter(([lastLoadedReport, currentReportState]) => lastLoadedReport.report === currentReportState),
        map(([lastLoadedReport, _]) => lastLoadedReport)
      );
    }

    return this.dashboardLoadedNew$;
  }

  private getObservable(): Observable<DashboardCombinedState> {
    if (!this.dashboard$) {
      this.dashboard$ = combineLatest([
        this.reportService.getReport(),
        this.store.pipe(select(getDashboardCombined))]
      ).pipe(
        filter(([_, dashboardState]) => dashboardState.config.config && !dashboardState.config.isLoading),
        map(([_, dashboardState]) => dashboardState),
        distinctUntilChanged()
      );
    }

    return this.dashboard$;
  }
}
